/**
 * Formats a number into a human-readable string with appropriate unit suffixes (K, M, B, T)
 * 
 * This utility function formats numbers according to French locale conventions and adds
 * appropriate unit suffixes for large numbers. For numbers under 1000, it simply formats
 * them with French locale. For larger numbers, it converts them to a more readable format
 * with appropriate unit suffixes (K for thousands, M for millions, etc.).
 * 
 * The function uses the following rules:
 * - Numbers < 1000: Displayed as is with French locale formatting
 * - Numbers >= 1000: Converted to appropriate unit (K, M, B, T) with:
 *   - 1 decimal place for numbers < 10K
 *   - No decimal places for numbers >= 10K
 * 
 * @param {number} num - The number to format
 * @returns {string} The formatted number string with appropriate unit suffix
 * 
 * @example
 * formatNumber(123)      // "123"
 * formatNumber(1234)     // "1,2K"
 * formatNumber(12345)    // "12,3K"
 * formatNumber(123456)   // "123,5K"
 * formatNumber(1234567)  // "1,2M"
 * formatNumber(12345678) // "12,3M"
 */
export const formatNumber = (num: number): string => {
  if (num < 1000) {
    return num.toLocaleString('fr-FR')
  }

  const units = ['K', 'M', 'B', 'T']
  const k = 1000
  const magnitude = Math.floor(Math.log(num) / Math.log(k))

  // Format with 1 decimal place for numbers >= 10K
  const formatted = num / k ** magnitude
  const rounded = Math.round(formatted * 10) / 10
  const isExactMultiple = num % (k ** magnitude) === 0

  let decimalPlaces = 0
  if (!isExactMultiple && rounded < 10) {
    decimalPlaces = 1
  }

  return `${rounded.toFixed(decimalPlaces).replace('.', ',')}${units[magnitude - 1]}`
} 
